import { useMemo, useRef } from "react";

// ── NO PAGE RELOAD AFTER A CLICK (2026-10-16) ─────────────────────────────────────────────────────
//
// Caleb, 2026-10-15: "every time I remove a user it has to reload the page and we don't want that.
// I need to be able to click and add and remove ... it doesn't have to reload the whole page." Then,
// 2026-10-16: standardize it. This file is the standard; docs/decisions/2026-10-16-no-page-reload-
// after-a-click.md says why, and quiet-reload-guard.py keeps new screens to it.
//
// THE RULE: a spinner is for data the screen has NEVER shown -- the first load, or a different
// thing (another campaign, another book, another date). After a click, what is on screen STAYS on
// screen. Either the change is drawn at once and saved behind it (optimistic, below), or the screen
// re-reads quietly and swaps the fresh copy in when it lands (useFirstLoad, below). It never blanks.

/** Show the spinner only the first time a given thing is loaded.
 *
 *    const first = useFirstLoad();
 *    useEffect(() => {
 *      const key = `${campaignId}:${bookId}`;
 *      if (first.isNew(key)) setLoading(true);      // first load / a different book: spinner
 *      fetch...then(() => { first.done(key); setLoading(false); });
 *    }, [campaignId, bookId, reload]);             // a `reload` bump re-reads QUIETLY
 */
export function useFirstLoad() {
  const shown = useRef<string | null>(null);
  return useMemo(() => ({
    isNew: (key: string) => shown.current !== key,
    done: (key: string) => { shown.current = key; },
  }), []);
}

/** Draw a change at once, save it behind, and put it back if the save fails.
 *
 *    await optimistic(
 *      () => setRows(rows.filter((r) => r.id !== id)),      // what the click means, on screen now
 *      () => api("/thing/remove", { ... }),                  // the save
 *      () => setRows(before),                                // undo, if the save failed
 *    );
 *
 *  It rethrows, so the caller still says what failed (a quiet failure that silently undoes the
 *  click is worse than a spinner). Use it when the screen can know the result without asking --
 *  a name added to a list, a row removed. When the server decides something the screen cannot
 *  guess (a tag that can close a whole door, a bulk rule), re-read quietly instead. */
export async function optimistic<T>(apply: () => void, save: () => Promise<T>, undo: () => void): Promise<T> {
  apply();
  try { return await save(); }
  catch (e) { undo(); throw e; }
}
